<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import type { MapboxStaticMarker } from "../utils/mapboxStatic";

const props = withDefaults(
  defineProps<{
    center?: [number, number];
    zoom?: number;
    retina?: boolean;
    markers?: MapboxStaticMarker[];
    alt?: string;
    padding?: string;
    priority?: boolean;
    // Consentement Mapbox injecté par l'hôte (web : cookie-consent ; mobile : toujours
    // autorisé en natif). Quand false, le slot #disabled est rendu à la place de l'image.
    mapboxAllowed?: boolean;
  }>(),
  {
    center: () => [0, 0],
    zoom: 12,
    retina: true,
    markers: () => [],
    alt: "",
    padding: undefined,
    priority: false,
    mapboxAllowed: true,
  }
);

const STEP_SIZE = 40;
const MAX_SIZE = 1280;
const containerRef = ref<HTMLElement>();
const { width: elementWidth, height: elementHeight } = useElementSize(containerRef);

const snapToStep = (value: number) => Math.min(MAX_SIZE, Math.ceil(value / STEP_SIZE) * STEP_SIZE);

const width = computed(() => snapToStep(elementWidth.value));
const height = computed(() => {
  if (elementHeight.value > 0) return snapToStep(elementHeight.value);
  return Math.round(width.value * 0.6);
});

// Le mode `auto` de Mapbox cadre sur la bounding box des marqueurs mais ne permet
// pas de plafonner le zoom (points proches = cadrage trop serré). On reproduit le
// cadrage en Web Mercator pour borner le zoom à `props.zoom`.
const TILE_SIZE = 512;
const AUTO_FIT_MARGIN = 1.2;
// Le marqueur est centré sur son point mais le pin visible occupe le haut de l'image (bas
// transparent), il « flotte » donc au-dessus du point. On réserve une marge supérieure
// généreuse pour ne pas le rogner, quasi nulle en bas. En pixels logiques de l'image.
const MARKER_FIT_PADDING = { top: 64, bottom: 16, x: 20 };

const mercatorY = (lat: number) => {
  const sin = Math.sin((lat * Math.PI) / 180);
  return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
};
const latFromMercatorY = (y: number) => (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;

const positioning = computed(() => {
  const autoFit = props.markers.length > 0 && props.center[0] === 0 && props.center[1] === 0;
  if (!autoFit) return mapboxStaticPositioning(props.center, props.zoom);

  const lngs = props.markers.map((m) => m.coordinates[0]);
  const lats = props.markers.map((m) => m.coordinates[1]);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);

  const lngFraction = ((maxLng - minLng) / 360) * AUTO_FIT_MARGIN;
  const latFraction = Math.abs(mercatorY(maxLat) - mercatorY(minLat)) * AUTO_FIT_MARGIN;
  const zoomToFit = (fraction: number, pixels: number) => (fraction > 0 ? Math.log2(pixels / TILE_SIZE / fraction) : props.zoom);
  const availWidth = Math.max(STEP_SIZE, width.value - 2 * MARKER_FIT_PADDING.x);
  const availHeight = Math.max(STEP_SIZE, height.value - MARKER_FIT_PADDING.top - MARKER_FIT_PADDING.bottom);
  const zoom = Math.max(0, Math.min(zoomToFit(lngFraction, availWidth), zoomToFit(latFraction, availHeight), props.zoom));

  // Centre en Web Mercator (la moyenne arithmétique des latitudes décalerait le cadrage vers
  // le nord et rognerait les marqueurs hauts), puis décalage vertical traduisant l'asymétrie
  // du padding en pixels — la réserve va surtout en haut.
  const worldPx = TILE_SIZE * 2 ** zoom;
  const verticalShiftPx = (MARKER_FIT_PADDING.top - MARKER_FIT_PADDING.bottom) / 2;
  const centerY = (mercatorY(minLat) + mercatorY(maxLat)) / 2 - verticalShiftPx / worldPx;
  const centerLng = (minLng + maxLng) / 2;
  return mapboxStaticPositioning([centerLng, latFromMercatorY(centerY)], zoom);
});

const src = computed(() => {
  if (!width.value) return undefined;
  return mapboxStaticImageUrl({
    positioning: positioning.value,
    width: width.value,
    height: height.value,
    markers: props.markers,
    retina: props.retina,
    padding: props.padding,
  });
});

// L'img n'apparaît qu'au client (src dépend de la mesure) : pendant le fetch Mapbox, le
// navigateur rendrait le texte alt par-dessus le fond. On garde l'image invisible jusqu'au
// load ; en erreur, l'alt redevient le repli visible.
const loaded = ref(false);
const failed = ref(false);
</script>

<template>
  <div ref="containerRef" class="relative bg-blue-900/20">
    <slot v-if="!mapboxAllowed" name="disabled" />
    <AppImagePlaceholder v-else-if="failed" variant="map" class="absolute inset-0 size-full" />
    <img
      v-else-if="src"
      :src="src"
      :alt="alt"
      :width="width"
      :height="height"
      :loading="priority ? 'eager' : 'lazy'"
      :fetchpriority="priority ? 'high' : 'auto'"
      decoding="async"
      class="absolute inset-0 size-full object-cover motion-safe:transition-opacity motion-safe:duration-150"
      :class="loaded || failed ? 'opacity-100' : 'opacity-0'"
      @load="loaded = true"
      @error="failed = true"
    />
    <slot />
  </div>
</template>
